<template>
	<ul class="map-labels" ref="mapLabels" 
		:class="[
			'map-labels',
			{ 'labels-visible': labelsVisible === true }
		]"
	>
		<li v-for="label in this.labels" :key="label.id" :ref="label.id"
			:class="[
				{ 'interior-label': label.area === 'interior' },
				{ 'exterior-label': label.area === 'exterior' },
				{ 'bold': label.bold === true },
				{ 'no-code': label.code === null || label.code.length === 0 },
				{ 'visible': label.range.min <= zoomLevel && label.range.max >= zoomLevel}
			]"
			:style="{
				'font-size' : label.size + 'px',
				'color' : label.color,
				'top' : label.position.y + '%',
				'left' : label.position.x + '%',
				'transform-origin' : '50% 50%',
				'transform' : 'translate(-50%, -50%) scale(' + (labelScale + label.scaleAdjust) + ')'
			}"
			v-on:click="selectLabel($event, label.code)"
		>
			<p class="label-stroke"
				:style="{
					'color' : label.outline,
					'text-stroke': '2px ' + label.outline,
					'transform' : 'translate(' + (50 - label.anchor.x) + '%, ' + (50 - label.anchor.y) + '%)'
				}"
			>{{ label.text }}</p>
			<p class="label-fill"
				:style="{
					'transform' : 'translate(' + (50 - label.anchor.x) + '%, ' + (50 - label.anchor.y) + '%)'
				}"
			>{{ label.text }}</p>
		</li>
	</ul>
</template>

<script>
	import { gsap } from 'gsap';

	export default {
		name: "MapLabels",
		props: {
			zoomFactor: Number,
			mapLoaded: Boolean,
			lastSelected: String
		},
		data: function() {
			return {
				sharedStore: window.store,
				labels: [],
				baseScale: 1,
				labelsVisible: false
			}
		},
		mounted: function() {
			this.calculateBaseScale();
		},
		created() {
			window.addEventListener("resize", this.onResize);
		},
		destroyed() {
			window.removeEventListener("resize", this.onResize);
		},
		watch: {
			"sharedStore.state.lastDataUpdate": function() {
				// filter out labels that have a 'hidden' flag
				let filtered = this.sharedStore.state.labelData.filter(label => label.hidden === false);
				this.labels = filtered;
				this.$forceUpdate();
				this.checkLabelsVisible();
			},
			mapLoaded: function() {
				this.calculateBaseScale();
				this.checkLabelsVisible();
			},
			lastSelected: function() {
				this.pulseLabel();
			},
			"sharedStore.state.languageCode": function() {
				this.updateLabelData();
			}
		},
		computed: {
			labelScale: function() {
				// baseScale / 2 acts as a maximum size, to prevent labels from getting too huge when zoomed very far out
				return Math.min(this.baseScale / this.zoomFactor, this.baseScale / 2);
			},
			zoomLevel: function() {
				if (this.zoomFactor <= 2) {
					return 0;
				} else if (this.zoomFactor <= 3.5) {
					return 1;
				} else if (this.zoomFactor <= 4.5) {
					return 2;
				} else {
					return 3;
				}
			}
		},
		methods: {
			updateLabelData: function() {
				let languageCode = this.sharedStore.state.languageCode;
				this.labels.forEach(label => {
					label.text = label["text_" + languageCode];
					label.size = label["size_" + languageCode];
				});
			},
			checkLabelsVisible: function() {
				if (this.labels.length && this.mapLoaded) {
					this.labelsVisible = true;
				}
			},
			selectLabel: function(e, prefix) {
				if (prefix) {
					this.$emit("selectLabel", e, prefix);
				}
			},
			pulseLabel: function() {
				this.labels.forEach((label) => {
					if (this.lastSelected === label.code) {
						let timeline = new gsap.timeline();

						timeline.to(label, {
							duration: 0.1,
							scaleAdjust: (this.labelScale * 0.2),
							onUpdate: () => {
								this.$forceUpdate();
							}
						});
						timeline.to(label, {
							duration: 0.2,
							scaleAdjust: 0,
							onUpdate: () => {
								this.$forceUpdate();
							}
						});
					}
				});
			},
			calculateBaseScale: function() {
				this.baseScale = this.$refs.mapLabels.offsetWidth / 400;
			},
			onResize: function() {
				setTimeout(() => {
					this.calculateBaseScale();
				}, 0);
			}
		}
	};
</script>

<style scoped lang="scss">
	.map-labels {
		position: absolute;
		top: 0;
		width: 100%;
		height: 0;
		margin: 0;
		padding: 0;
		padding-top: 122.9%;
		pointer-events: none;

		transition: opacity 0.5s;
		opacity: 0;

		&.labels-visible {
			opacity: 1;
		}
	}

	li {
		position: absolute;
		text-align: left;
		font-weight: 500;
		line-height: 100%;
		pointer-events: auto;
		cursor: pointer;

		transition: opacity 0.5s;
		opacity: 0;

		&.bold {
			font-weight: 700;
		}

		&.visible {
			opacity: 1;
		}

		&.no-code {
			pointer-events: none;
		}

		p {
			position: absolute;
			top: 0;
			left: 0;
			margin: 0;
			padding: 0;
			white-space: pre-wrap;
		}

		.label-stroke {
			position: relative;
		}
	}
</style>
